import { useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { Download, Image as ImageIcon, FileCode, Palette } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * BrandAssetsCard — Panel Maestro · descarga del logotipo en SVG/PNG/JPG.
 * NOTA seguridad: el SVG se renderiza con JSX declarativo (NO dangerouslySetInnerHTML)
 * para evitar XSS. Solo se serializa a string al exportar.
 */

// SVG canónico AutoGlow · alineado 1:1 con AutoGlowBrand inline (Feb 2026).
// Cambios vs versión anterior:
//   - Familia tipográfica → Inter Tight (alineada con la wordmark inline).
//   - "ECOSYSTEM" en muted (no aqua) — el oro es el único acento.
//   - Sparkle reposicionada sobre la "A" + arco dorado debajo del wordmark.
function BrandSVG({ variant = "dark", widthPx = 360 }) {
  const bg = variant === "dark" ? "#1A1A1A" : "#FFFFFF";
  const autoColor = variant === "dark" ? "#FFFFFF" : "#1A1A1A";
  const tagColor = variant === "dark" ? "#8B93A7" : "#94A3B8";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 240" width={widthPx} aria-label="AutoGlow Ecosystem logo">
      <rect width="600" height="240" fill={bg} />
      {/* Sparkle dorada arriba sobre la "A" — misma forma que la inline */}
      <g transform="translate(135 50)">
        <path d="M0 -16 L3.5 -3.5 L16 0 L3.5 3.5 L0 16 L-3.5 3.5 L-16 0 L-3.5 -3.5 Z" fill="#FFB703" />
      </g>
      {/* Línea 1: AutoGlow · Inter Tight 700 */}
      <g fontFamily="'Inter Tight', Inter, system-ui, sans-serif" fontWeight="700" fontSize="92" letterSpacing="-2.5">
        <text x="115" y="148" fill={autoColor}>Auto</text>
        <text x="315" y="148" fill="#FFB703">Glow</text>
      </g>
      {/* Arco dorado dinámico debajo del wordmark */}
      <path d="M120 170 Q300 195 485 170" fill="none" stroke="#FFB703" strokeWidth="3" strokeLinecap="round" opacity="0.92" />
      {/* Línea 2: ECOSYSTEM · muted (no aqua) */}
      <text x="300" y="215" textAnchor="middle"
        fontFamily="'Inter Tight', Inter, system-ui, sans-serif" fontWeight="600" fontSize="22"
        letterSpacing="11" fill={tagColor}>ECOSYSTEM</text>
    </svg>
  );
}

/** Serializa el SVG inline (que viene del DOM) como string para descarga. */
function serializeSvg(svgElement) {
  return new XMLSerializer().serializeToString(svgElement);
}

function svgStringToPng(svgString, scale = 4) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 600 * scale;
      canvas.height = 240 * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => { URL.revokeObjectURL(url); resolve(b); }, "image/png");
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function svgStringToJpg(svgString, scale = 4) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 600 * scale;
      canvas.height = 240 * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => { URL.revokeObjectURL(url); resolve(b); }, "image/jpeg", 0.95);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function BrandAssetsCard() {
  const svgRef = useRef(null);
  const [variant, setVariant] = useState("dark");
  const [busy, setBusy] = useState(null);

  const suffix = variant === "dark" ? "dark" : "light";

  const getSvgString = () => {
    if (!svgRef.current) return "";
    return '<?xml version="1.0" encoding="UTF-8"?>\n' + serializeSvg(svgRef.current);
  };

  const onDownloadSVG = () => {
    const s = getSvgString();
    if (!s) return;
    const blob = new Blob([s], { type: "image/svg+xml;charset=utf-8" });
    downloadBlob(blob, `autoglow-logo-${suffix}.svg`);
    toast.success("Logo SVG descargado");
  };
  const onDownloadPNG = async () => {
    setBusy("png");
    try {
      const blob = await svgStringToPng(getSvgString(), 4);
      downloadBlob(blob, `autoglow-logo-${suffix}.png`);
      toast.success("Logo PNG descargado (alta resolución)");
    } catch (err) {
      console.error("PNG export failed", err);
      toast.error("Error generando PNG");
    } finally { setBusy(null); }
  };
  const onDownloadJPG = async () => {
    setBusy("jpg");
    try {
      const blob = await svgStringToJpg(getSvgString(), 4);
      downloadBlob(blob, `autoglow-logo-${suffix}.jpg`);
      toast.success("Logo JPG descargado");
    } catch (err) {
      console.error("JPG export failed", err);
      toast.error("Error generando JPG");
    } finally { setBusy(null); }
  };

  return (
    <section className="ag-card p-6" data-testid="brand-assets-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)] flex items-center gap-2">
            <Palette size={12} weight="fill" /> Identidad de marca
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tighter mt-1">Logo AutoGlow · descargas</h2>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2">
            Descarga el logotipo oficial en alta calidad para usarlo en redes sociales, papelería, presentaciones o edición externa.
          </p>
        </div>
        <div className="flex gap-1 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded p-1" role="tablist" data-testid="brand-variant-toggle">
          <button onClick={()=>setVariant("dark")}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded transition-all ${variant === "dark" ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-bold" : "text-[var(--ag-text-dim)] hover:text-white"}`}
            data-testid="brand-variant-dark">Fondo oscuro</button>
          <button onClick={()=>setVariant("light")}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded transition-all ${variant === "light" ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-bold" : "text-[var(--ag-text-dim)] hover:text-white"}`}
            data-testid="brand-variant-light">Fondo claro</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 items-start">
        <div
          className="border border-[var(--ag-border)] rounded-sm overflow-hidden flex items-center justify-center"
          style={{ backgroundColor: variant === "dark" ? "#1A1A1A" : "#FFFFFF", minHeight: 180 }}
          data-testid="brand-preview">
          {/* SVG declarativo (NO dangerouslySetInnerHTML) */}
          <svg
            ref={svgRef}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 600 240"
            style={{ width: "100%", maxWidth: 360, height: "auto" }}
            aria-label="AutoGlow Ecosystem logo preview"
          >
            <rect width="600" height="240" fill={variant === "dark" ? "#1A1A1A" : "#FFFFFF"} />
            {/* Sparkle dorada sobre la "A" */}
            <g transform="translate(135 50)">
              <path d="M0 -16 L3.5 -3.5 L16 0 L3.5 3.5 L0 16 L-3.5 3.5 L-16 0 L-3.5 -3.5 Z" fill="#FFB703" />
            </g>
            {/* Wordmark · Inter Tight 700 */}
            <g fontFamily="'Inter Tight', Inter, system-ui, sans-serif" fontWeight="700" fontSize="92" letterSpacing="-2.5">
              <text x="115" y="148" fill={variant === "dark" ? "#FFFFFF" : "#1A1A1A"}>Auto</text>
              <text x="315" y="148" fill="#FFB703">Glow</text>
            </g>
            {/* Arco dorado dinámico debajo */}
            <path d="M120 170 Q300 195 485 170" fill="none" stroke="#FFB703" strokeWidth="3" strokeLinecap="round" opacity="0.92" />
            {/* ECOSYSTEM muted (alineado con la wordmark inline) */}
            <text x="300" y="215" textAnchor="middle"
              fontFamily="'Inter Tight', Inter, system-ui, sans-serif" fontWeight="600" fontSize="22"
              letterSpacing="11" fill={variant === "dark" ? "#8B93A7" : "#94A3B8"}>ECOSYSTEM</text>
          </svg>
        </div>
        <div className="space-y-2">
          <Button onClick={onDownloadSVG} variant="outline"
            className="w-full justify-between bg-transparent border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]"
            data-testid="brand-download-svg">
            <span className="flex items-center gap-2"><FileCode size={16} weight="duotone" className="text-[var(--ag-aqua-soft)]" /> SVG · Vectorial editable</span>
            <Download size={14} />
          </Button>
          <Button onClick={onDownloadPNG} disabled={busy === "png"} variant="outline"
            className="w-full justify-between bg-transparent border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]"
            data-testid="brand-download-png">
            <span className="flex items-center gap-2"><ImageIcon size={16} weight="duotone" className="text-[var(--ag-gold)]" /> PNG · 2400×800 transparente</span>
            <Download size={14} />
          </Button>
          <Button onClick={onDownloadJPG} disabled={busy === "jpg"} variant="outline"
            className="w-full justify-between bg-transparent border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]"
            data-testid="brand-download-jpg">
            <span className="flex items-center gap-2"><ImageIcon size={16} weight="duotone" className="text-emerald-400" /> JPG · 2400×800 fondo blanco</span>
            <Download size={14} />
          </Button>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-3 leading-relaxed">
            <b className="text-[var(--ag-text)]">SVG</b> — formato vectorial, escala infinita sin perder calidad. Ideal para Illustrator/Inkscape.<br/>
            <b className="text-[var(--ag-text)]">PNG</b> — Rasterizado a 4× con transparencia. Para presentaciones y web.<br/>
            <b className="text-[var(--ag-text)]">JPG</b> — Comprimido con fondo blanco. Para impresión o entornos sin transparencia.
          </div>
        </div>
      </div>
    </section>
  );
}
